import React, { useEffect, useState } from 'react'
import { Bar } from 'react-chartjs-2'
import { BarElement, CategoryScale, Chart as ChartJS, Legend, LinearScale, Title, Tooltip } from 'chart.js'
import zoomPlugin from 'chartjs-plugin-zoom'
import { IconButton } from '@mui/material'
import { OpenInFull } from '@mui/icons-material'
import FullScreenModal from 'components/FullScreenModal'
import PropTypes from 'prop-types'
import DashboardCard from 'components/Statistics/DashboardCard'
import ChartDataLabels from 'chartjs-plugin-datalabels'
import BarChartSkeleton from './skeleton'

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels,
  zoomPlugin,
)

const sizes = {
  small: {
    width: 80,
    height: 80,
  },
  medium: {
    width: 120,
    height: 120,
  },
  large: {
    width: 200,
    height: 200,
  },
  fullscreen: {
    width: '65',
    height: '65',
  },
}

const BarChart = (
  {
    data,
    title,
    orientation = 'vertical',
    displayLegend = false,
    size = 'medium',
    isLoading,
    displayEmptyColumns = true,
    displayValues = true,
  },
) => {
  const [isFullScreen, setIsFullScreen] = useState(false)

  const { height } = sizes[size]

  const chartData = displayEmptyColumns
    ? {
      ...data,
      datasets: data.datasets.map((dataset) => ({
        ...dataset,
        minBarLength: 5,
      })),
    }
    : data

  const options = {
    responsive: true,
    maintainAspectRatio: true,
    indexAxis: orientation === 'horizontal' ? 'y' : 'x',
    transitions: {
      zoom: {
        animation: {
          duration: 1000,
          easing: 'easeOutCubic',
        },
      },
    },
    plugins: {
      legend: {
        display: displayLegend,
      },
      datalabels: {
        display: displayValues ? 'auto' : false,
        anchor: 'end',
        align: 'end',
        offset: 1,
      },
      zoom: {
        pan: {
          enabled: true,
          mode: 'x',
        },
        zoom: {
          pinch: {
            enabled: true,
          },
          wheel: {
            enabled: true,
          },
          mode: 'x',
        },
      },
    },
  }

  const toggleFullScreen = () => {
    setIsFullScreen(!isFullScreen)
  }

  useEffect(() => {
    const handleKeyPress = (e) => {
      const escapeKeys = ['Escape', 'Esc']
      if (isFullScreen && escapeKeys.includes(e.key)) {
        toggleFullScreen()
      }
    }

    window.addEventListener('keydown', handleKeyPress)

    return () => {
      window.removeEventListener('keydown', handleKeyPress)
    }
  }, [isFullScreen, toggleFullScreen])


  return (
    <DashboardCard
      title={title}
      actions={
        <IconButton
          aria-label='toggle full screen'
          onClick={toggleFullScreen}
          title='Expandir'
        >
          <OpenInFull style={{ width: '24', height: '24' }} />
        </IconButton>
      }
    >
      {isLoading ? <BarChartSkeleton /> : <Bar options={options} data={chartData} height={height} />}

      <FullScreenModal title={title} open={isFullScreen} handleClose={toggleFullScreen}>
        <Bar options={options} data={chartData} />
      </FullScreenModal>
    </DashboardCard>
  )
}

BarChart.propTypes = {
  data: PropTypes.object.isRequired,
  title: PropTypes.string.isRequired,
  orientation: PropTypes.oneOf(['horizontal', 'vertical']),
  displayLegend: PropTypes.bool,
  size: PropTypes.oneOf(['small', 'medium', 'large', 'fullscreen']),
  isLoading: PropTypes.bool.isRequired,
  displayEmptyColumns: PropTypes.bool,
  displayValues: PropTypes.bool,
}

BarChart.defaultProps = {
  orientation: 'vertical',
  displayLegend: false,
  size: 'medium',
  displayEmptyColumns: true,
  displayValues: true,
}

export default BarChart
